import { client } from './api'
import gql from 'graphql-tag'
import { AccountDisplayInfo } from './session'

export const getAllOutstreamPlacementCollectionSummariesQuery = gql`
  query {
    outstreamPlacementCollections_v2 {
      id
      title
    }
  }
`

export const getOutstreamPlacementCollectionTagsQuery = gql`
  query ($collectionId: String) {
    outstreamPlacementCollections_v2(byId: $collectionId) {
      tags
    }
    session {
      groupNames
    }
  }
`

export interface OutstreamPlacementCollectionSummary {
  id: string
  title: string
}

export enum OutstreamPlacementIntegrationType {
  CUSTOM = 'custom',
  FIXED_POSITION = 'fixedPosition',
  R2B2_REPLACEMENT = 'r2b2Replacement',
  R2B2_PLUS = 'r2b2Plus',
  R2B2_ONE = 'r2b2One',
  R2B2_FLOATING = 'r2b2Floating',
  MINIMAL_IMA = 'minimalIma',
  MULTI_POSITION_WITH_FLOATING = 'multiPositionWithFloating',
  IN_READ_TO_R2B2 = 'inReadToR2B2',
}

export interface OutstreamPlacementDocumentation {
  text: string
  aiGenerated: boolean
}

export interface OutstreamPlacementInfo {
  id: string
  title: string
  creationDate: string
  tags: string[]
  integration?: string
  videoContentCollectionId?: string
  whitelistGlobs?: string[]
  blacklistGlobs?: string[]
  targetingRules?: string[]
  maximumAdLength?: number
  bannerDurationSeconds?: number
  integrationType: OutstreamPlacementIntegrationType
  integrationTypeConfig: any
  documentation?: OutstreamPlacementDocumentation
}

export interface OutstreamPlacementCollectionSummaryResponse {
  outstreamPlacementCollections_v2: OutstreamPlacementCollectionSummary[]
}

export interface OutstreamPlacementCollectionTagsResponse {
  outstreamPlacementCollections_v2: { tags: string[] }[]
  session: AccountDisplayInfo
}

export const getOutstreamPlacementsQuery = gql`
  query ($collectionId: String, $startFromId: String) {
    outstreamPlacementCollections_v2(byId: $collectionId) {
      id
      tags
      assets(startFromId: $startFromId) {
        id
        title
        creationDate
        tags
        maximumAdLength
        bannerDurationSeconds
        integration
        videoContentCollectionId
        whitelistGlobs
        blacklistGlobs
        targetingRules
        integrationType
        integrationTypeConfig
        documentation {
          text
          aiGenerated
        }
      }
    }
  }
`

// ponytail: first assets page (1000) per collection, paginate if an account outgrows it
export const getAllOutstreamPlacementIntegrationsQuery = gql`
  query {
    outstreamPlacementCollections_v2 {
      assets {
        id
        title
        integration
        integrationType
      }
    }
  }
`

export interface OutstreamPlacementResponse {
  outstreamPlacementCollections_v2: {
    id: string
    assets: OutstreamPlacementInfo[]
  }[]
}

const createOutstreamPlacementCollectionMutation = gql`
  mutation ($title: String!) {
    outstreamPlacementCollections_v2 {
      create(title: $title)
    }
  }
`

const renameOutstreamPlacementCollectionMutation = gql`
  mutation ($id: String, $title: String!) {
    outstreamPlacementCollections_v2(byId: $id) {
      update(title: $title)
    }
  }
`

const deleteOutstreamPlacementCollectionMutation = gql`
  mutation ($id: String) {
    outstreamPlacementCollections_v2(byId: $id) {
      delete
    }
  }
`

const deleteOutstreamPlacementMutation = gql`
  mutation ($collectionId: String, $id: String) {
    outstreamPlacementCollections_v2(byId: $collectionId) {
      assets(byId: $id) {
        delete
      }
    }
  }
`

const createOutstreamPlacementMutation = gql`
  mutation (
    $collectionId: String!
    $title: String!
    $integration: String!
    $videoContentCollectionId: String
    $tags: [String!]!
    $whitelistGlobs: [String]!
    $blacklistGlobs: [String]!
    $targetingRules: [String!]
    $maximumAdLength: Int
    $bannerDurationSeconds: Int
    $integrationType: OutstreamPlacementIntegrationType
    $integrationTypeConfig: JSONObject
  ) {
    outstreamPlacementCollections_v2(byId: $collectionId) {
      assets {
        create(
          title: $title
          integration: $integration
          videoContentCollectionId: $videoContentCollectionId
          tags: $tags
          whitelistGlobs: $whitelistGlobs
          blacklistGlobs: $blacklistGlobs
          targetingRules: $targetingRules
          maximumAdLength: $maximumAdLength
          bannerDurationSeconds: $bannerDurationSeconds
          integrationType: $integrationType
          integrationTypeConfig: $integrationTypeConfig
        )
      }
    }
  }
`

const updateOutstreamPlacementMutation = gql`
  mutation (
    $collectionId: String!
    $id: String!
    $title: String!
    $integration: String!
    $videoContentCollectionId: String
    $tags: [String!]!
    $whitelistGlobs: [String]!
    $blacklistGlobs: [String]!
    $targetingRules: [String!]
    $maximumAdLength: Int
    $bannerDurationSeconds: Int
    $integrationType: OutstreamPlacementIntegrationType
    $integrationTypeConfig: JSONObject
  ) {
    outstreamPlacementCollections_v2(byId: $collectionId) {
      assets(byId: $id) {
        update(
          title: $title
          integration: $integration
          videoContentCollectionId: $videoContentCollectionId
          tags: $tags
          whitelistGlobs: $whitelistGlobs
          blacklistGlobs: $blacklistGlobs
          targetingRules: $targetingRules
          maximumAdLength: $maximumAdLength
          bannerDurationSeconds: $bannerDurationSeconds
          integrationType: $integrationType
          integrationTypeConfig: $integrationTypeConfig
        )
      }
    }
  }
`

export async function createOutstreamPlacementCollection(title: string) {
  return client.mutate({
    mutation: createOutstreamPlacementCollectionMutation,
    variables: { title },
  })
}

export async function renameOutstreamPlacementCollection(
  collectionId: string,
  newTitle: string
) {
  return client.mutate({
    mutation: renameOutstreamPlacementCollectionMutation,
    variables: {
      id: collectionId,
      title: newTitle,
    },
  })
}

export async function deleteOutstreamPlacementCollection(collectionId: string) {
  return client.mutate({
    mutation: deleteOutstreamPlacementCollectionMutation,
    variables: { id: collectionId },
  })
}

export async function createOutstreamPlacement(
  collectionId: string,
  title: string,
  integration: string,
  videoContentCollectionId: string | null,
  tags: string[],
  whitelistGlobs: string[],
  blacklistGlobs: string[],
  maximumAdLength: number | null,
  bannerDurationSeconds: number | null,
  integrationType: OutstreamPlacementIntegrationType,
  integrationTypeConfig: any,
  targetingRules: string[]
): Promise<string> {
  const mutationResult = await client.mutate({
    mutation: createOutstreamPlacementMutation,
    variables: {
      collectionId,
      title,
      integration,
      videoContentCollectionId,
      tags,
      whitelistGlobs,
      blacklistGlobs,
      targetingRules,
      maximumAdLength,
      bannerDurationSeconds,
      integrationType,
      integrationTypeConfig,
    },
  })
  return mutationResult.data.outstreamPlacementCollections_v2.assets.create
}

export async function updateOutstreamPlacement(
  collectionId: string,
  id: string,
  title: string,
  integration: string,
  videoContentCollectionId: string | null,
  tags: string[],
  whitelistGlobs: string[],
  blacklistGlobs: string[],
  maximumAdLength: number | null,
  bannerDurationSeconds: number | null,
  integrationType: OutstreamPlacementIntegrationType,
  integrationTypeConfig: any,
  targetingRules: string[]
): Promise<string> {
  const mutationResult = await client.mutate({
    mutation: updateOutstreamPlacementMutation,
    variables: {
      collectionId,
      id,
      title,
      integration,
      videoContentCollectionId,
      tags,
      whitelistGlobs,
      blacklistGlobs,
      targetingRules,
      maximumAdLength,
      bannerDurationSeconds,
      integrationType,
      integrationTypeConfig,
    },
  })
  return mutationResult.data.outstreamPlacementCollections_v2.assets.update
}

export async function deleteOutstreamPlacement(
  collectionId: string,
  id: string
) {
  return client.mutate({
    mutation: deleteOutstreamPlacementMutation,
    variables: { id, collectionId },
  })
}
